import type { AxiosInstance } from 'axios';
import { apiClient } from '../client/client';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type {
  UtmSource,
  UtmVisitRecordedViewModel,
  UtmStatsViewModel,
} from '../types/utm';

export interface UtmVisitCreateRequest {
  source: UtmSource;
  listingId?: string | null;
  path: string;
}

/**
 * UTM tracking endpoints (feature 010, US3).
 *
 * `record` is public and fire-and-forget: network or 4xx/5xx errors must never
 * leak to the UI (FR-015). A failed call yields `{ recorded: false, reason: null }`.
 *
 * `getStats` is admin-only; callers are expected to be on an authenticated admin
 * page and Axios will attach the JWT through the shared interceptor.
 */
export class UtmVisitService {
  private static readonly RECORD_URL = '/api/UtmVisits';
  private static readonly STATS_URL = '/api/Admin/UtmVisits/stats';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async record(request: UtmVisitCreateRequest): Promise<UtmVisitRecordedViewModel> {
    try {
      const { data } = await this.client.post<
        ServiceResultT<UtmVisitRecordedViewModel>
      >(UtmVisitService.RECORD_URL, request);
      if (data?.data) {
        return data.data;
      }
      return { recorded: false, reason: null };
    } catch {
      return { recorded: false, reason: null };
    }
  }

  async getStats(from: string, to: string): Promise<UtmStatsViewModel> {
    const { data } = await this.client.get<ServiceResultT<UtmStatsViewModel>>(
      UtmVisitService.STATS_URL,
      { params: { from, to } },
    );
    if (!data?.data) {
      throw new Error(data?.message ?? 'UTM istatistikleri alınamadı.');
    }
    return data.data;
  }
}

export const utmVisitService = new UtmVisitService();
